import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  API,
  CampaignWithPreviewDTO,
  ClientInfoDTO,
  FixedAspectRatioImageDTO,
  FrameDTO,
  SingleFileUpload,
  TimingStatus,
  UserDTO,
} from '../../services/api/api';
import { ErrorService } from '../../services/error.service';
import { UserService } from '../../services/user.service';
import { UtilService } from '../../services/util.service';
import { SbDateTime, SbLocalDateTime } from '../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { PermissionService } from '../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { Clipping } from '../../components/image/model';
import { ImageUploadModel } from '../../services/gui/image/model';
import { ImageUploadService } from '../../services/gui/image/image-upload.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../services/gui/modal/modal.service';
import { SbIconName } from '@ngx-seatback/icons';

export interface MissingOptions {
  permission: string;
  link: string;
  label: string;
  icon: SbIconName;
}

@Component({
  selector: 'sb-page-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit {
  companyId: number;
  loading: boolean;
  reportData: FrameDTO[];
  interval: number;
  selTiming: TimingStatus;

  campaigns: CampaignWithPreviewDTO[];
  from: SbDateTime;
  till: SbDateTime;
  today: DateTime = DateTime.local();

  // variables Image upload
  imageUpload: ImageUploadModel = new ImageUploadModel();
  protected hasOpenMissingOptions: boolean = true;
  protected hasDevices: boolean = false;
  protected hideInfoTile: boolean = false;
  protected loadingClientInfo: boolean = true;
  protected missingOptions: MissingOptions[] = [];

  constructor(
    protected userStatus: UserService,
    private api: API,
    public errorHandler: ErrorService,
    public util: UtilService,
    public permissionService: PermissionService,
    private imageUploadService: ImageUploadService,
    public modalService: ModalService,
    public translate: TranslateService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.companyId = undefined;
    this.reportData = undefined;
    this.selTiming = null;
    this.loading = true;
    this.interval = 604800;
    this.campaigns = [];
    this.from = new SbDateTime(DateTime.local().startOf('day').minus({ day: 91 }));
    this.till = new SbDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    this.refreshFromServer();

    this.api
      .shared_Client_retrieveInfo()
      .pipe(
        finalize(() => {
          this.loadingClientInfo = false;
        })
      )
      .subscribe({
        next: clientInfo => {
          this.hasDevices = clientInfo.existingTypeInstance.includes('DeviceDTO');
          this.processClientInfo(clientInfo);
        },
      });
  }

  private processClientInfo(clientInfo: ClientInfoDTO) {
    const missingOptions = [] as MissingOptions[];
    const currentlyFilled = clientInfo.existingTypeInstance;
    const currentlyMissing = clientInfo.missingTypeInstance;
    let hasMissingOption: boolean = false;

    if (currentlyFilled.includes('CampaignDTO') && currentlyMissing.includes('ContentShareDTO')) {
      missingOptions.push({
        permission: this.permissionService.Permission.content_share_edit,
        link: this.util.RoutingID.contentShare,
        icon: 'management',
        label: this.translate.instant('lang.globals.contentShare'),
      });
      if (!this.permissionService.hasPermission(this.permissionService.Permission.content_share_edit)) {
        hasMissingOption = true;
      }
    }
    if (currentlyMissing.includes('DeviceDTO') && currentlyFilled.includes('ChannelDTO')) {
      missingOptions.push({
        permission: this.permissionService.Permission.device_create,
        link: this.util.RoutingID.devices,
        icon: 'playerOnline',
        label: this.translate.instant('lang.globals.devices'),
      });
      if (!this.permissionService.hasPermission(this.permissionService.Permission.device_create)) {
        hasMissingOption = true;
      }
    }
    if (currentlyFilled.includes('DeviceDTO')) {
      if (currentlyMissing.includes('PlaybackScheduleDTO') || currentlyMissing.includes('PlaybackScheduleSlotDTO')) {
        missingOptions.push({
          permission: this.permissionService.Permission.playback_schedule_edit,
          link: this.util.RoutingID.schedules,
          icon: 'schedules',
          label: this.translate.instant('nav.network.schedules'),
        });
        if (!this.permissionService.hasPermission(this.permissionService.Permission.playback_schedule_edit)) {
          hasMissingOption = true;
        }
      }
      if (currentlyMissing.includes('NetworkShareDTO')) {
        missingOptions.push({
          permission: this.permissionService.Permission.network_share_edit,
          link: this.util.RoutingID.networkShare,
          icon: 'networkShare',
          label: this.translate.instant('lang.headlines.networkshare'),
        });
        if (!this.permissionService.hasPermission(this.permissionService.Permission.network_share_edit)) {
          hasMissingOption = true;
        }
      }
    }

    if (missingOptions.length === 0) {
      this.hideInfoTile = true;
    }
    this.missingOptions = missingOptions;
    this.hasOpenMissingOptions = hasMissingOption;
  }

  getFromDate(days: number) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }

  refreshFromServer() {
    // this.selTiming = TimingStatus.Present;
    if (this.permissionService.hasPermission(this.permissionService.Permission.ss_nm_reporting)) {
      this.getNetworkData();
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.campaign_list)) {
      this.api
        .ds_cm_Campaign_retrieveAllFrontendEnhanced({
          timingStatus: this.selTiming ? [this.selTiming] : null,
          company: this.companyId ? [this.companyId] : null,
          from: new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 8 })),
          till: new SbLocalDateTime(DateTime.local().startOf('day').plus({ day: 8 })),
        })
        .subscribe({
          next: data => {
            this.campaigns = data;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }

    this.companyId = this.userStatus.user.client.id;
  }

  getNetworkData() {
    if (this.permissionService.hasPermission(this.permissionService.Permission.ss_nm_reporting)) {
      this.api
        .ss_nm_Reporting_deviceStatusCountHistory({
          from: this.from,
          till: this.till,
          productive: true,
          interval: this.interval,
        })
        .subscribe({
          next: data => {
            this.reportData = data.buckets[0].frames;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  setInterval(interval) {
    this.interval = interval;
    this.getNetworkData();
  }

  setTimeFrame(dates?) {
    if (dates) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.getNetworkData();
    }
  }

  // Profile Picture upload / change

  pickProfileImage() {
    this.imageUpload?.destroySubscriptions();
    this.api
      .shared_User_retrieve()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (user: UserDTO) => {
          this.imageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.profilePicture'));
          this.imageUpload.imageTransfer.image = user?.avatar;
          this.imageUpload.subClippingChange = this.imageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.clippingChange(res);
          });
          this.imageUpload.subFileChange = this.imageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.fileChange(file);
          });
          this.imageUpload.subDeletePress = this.imageUpload.imageTransfer.deletePress.subscribe(() => {
            this.deletePress();
          });
          this.imageUpload.subModalClosed = this.imageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.uploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clippingChange(pClipping: Clipping) {
    if (pClipping) {
      this.applyClipping(pClipping);
      const transImage: FixedAspectRatioImageDTO = this.imageUpload.imageTransfer.image;
      this.imageUpload.imageTransfer.image = null;
      this.imageUpload.imageTransfer.uploading = true;
      this.api
        .shared_User_clipAvatar(transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
            this.imageUpload.imageTransfer.image = aspectRatioImage;
            this.imageUpload.imageTransfer.uploading = false;
            this.userStatus.retrieveUser();
            this.refreshFromServer();
          },
          error: error => {
            this.modalService.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  applyClipping(pClipping: Clipping) {
    this.imageUpload.imageTransfer.image.clippingTLX = pClipping.clippingTLX;
    this.imageUpload.imageTransfer.image.clippingTLY = pClipping.clippingTLY;
    this.imageUpload.imageTransfer.image.clippingBRX = pClipping.clippingBRX;
    this.imageUpload.imageTransfer.image.clippingBRY = pClipping.clippingBRY;
  }

  fileChange(file: SingleFileUpload) {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;
    this.api
      .shared_User_uploadAvatar(file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
          this.imageUpload.imageTransfer.image = aspectRatioImage;
          this.imageUpload.imageTransfer.uploading = false;
          this.userStatus.retrieveUser();
          this.refreshFromServer();
        },
        error: error => {
          this.modalService.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  deletePress() {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;
    this.api
      .shared_User_deleteAvatar()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.imageUpload.imageTransfer.image = null;
          }
          this.imageUpload.imageTransfer.uploading = false;
          this.userStatus.retrieveUser();
          this.refreshFromServer();
        },
        error: error => {
          this.modalService.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  uploadModalClosed() {
    this.imageUpload.destroySubscriptions();
    this.userStatus.retrieveUser();
    this.refreshFromServer();
  }
}
